import { useCallback, useEffect, useMemo } from "react";

import { differenceInDays } from "date-fns";
import { compact, isEqual } from "lodash";

import { gql } from "@apollo/client";
import { usePrevious } from "@hooks/use-previous";
import {
  type GetSalesChartReport,
  type GetSalesChartReportVariables,
} from "@north-beam/nb-client-side";
import { type IGranularity } from "@north-beam/nb-common";

import { MAX_DAYS } from "..";
import { useAdsetFilters, useCampaignFilters } from "../atoms";

import { type SalesReportProps, useSalesReport } from "./use-sales-report";

export const useSalesChartReport = (
  granularity: IGranularity,
  variables: SalesReportProps,
) => {
  const state = useSalesReport<
    GetSalesChartReport,
    GetSalesChartReportVariables
  >(granularity, variables, GET_SALES_REPORT);

  const [campaignFilters] = useCampaignFilters();
  const [adsetFilters] = useAdsetFilters();

  const prevAdsetFilters = usePrevious(adsetFilters);
  const prevCampaignFilters = usePrevious(campaignFilters);

  const filters = useMemo(() => {
    return {
      ...(campaignFilters?.length && {
        campaignFilters: compact(campaignFilters),
      }),
      ...(adsetFilters?.length && { adsetFilters: compact(adsetFilters) }),
    };
  }, [adsetFilters, campaignFilters]);

  const loadAllData = useCallback(() => {
    return state.loadAllData(filters);
  }, [filters, state]);

  const loadOnlyAdData = useCallback(() => {
    return state.loadOnlyAdData(filters);
  }, [filters, state]);

  const loadOnlyAdsetsData = useCallback(() => {
    return state.loadOnlyAdsetsData(filters);
  }, [filters, state]);

  const loadOnlyCampaignData = useCallback(() => {
    return state.loadOnlyCampaignData(filters);
  }, [filters, state]);

  useEffect(() => {
    if (
      isEqual(prevAdsetFilters ?? [], adsetFilters ?? []) &&
      isEqual(prevCampaignFilters ?? [], campaignFilters ?? [])
    ) {
      return;
    }

    const startDate = new Date(variables.dateRange.startDate);
    const endDate = new Date(variables.dateRange.endDate);
    if (differenceInDays(endDate, startDate) > MAX_DAYS) {
      switch (granularity) {
        case "ad":
          loadOnlyAdData();

          break;
        case "adset":
          loadOnlyAdsetsData();

          break;
        case "campaign":
        case "platform":
          loadOnlyCampaignData();

          break;
      }
    } else {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignFilters, adsetFilters]);

  return {
    ...state,
    loadAllData,
    loadOnlyAdData,
    loadOnlyAdsetsData,
    loadOnlyCampaignData,
  };
};

const GET_SALES_REPORT = gql`
  query GetSalesChartReport(
    $dateRange: JSONObject!
    $attributionWindow: String!
    $attributionMethod: String!
    $compareDateRange: JSONObject
    $granularity: String
    $nbAccountingMode: String
    $timeGranularity: String
    $attributionWindows: [String!]
    $fakeCall: Boolean
    $campaignFilters: [String!]
    $adsetFilters: [String!]
  ) {
    me {
      salesReportChart(
        dateRange: $dateRange
        attributionWindow: $attributionWindow
        attributionMethod: $attributionMethod
        compareDateRange: $compareDateRange
        granularity: $granularity
        nbAccountingMode: $nbAccountingMode
        timeGranularity: $timeGranularity
        attributionWindows: $attributionWindows
        fakeCall: $fakeCall
        campaignFilters: $campaignFilters
        adsetFilters: $adsetFilters
      ) {
        rows {
          dimensions
          dailyMetrics
          forecastV1Daily
        }
        startDate
        endDate
        hasV1Forecasts
        customGoals {
          id
          displayName
        }
      }
    }
  }
`;
